import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  api,
  ApiError,
  setUnauthenticatedHandler,
  type SessionInfo,
  type User,
} from '../api/client'
import { AuthContext, type AuthValue } from './context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const info: SessionInfo = await api.me()
      setUser(info.user)
    } catch (cause) {
      // 401 is the ordinary "not signed in" answer, not a failure to report.
      if (!(cause instanceof ApiError && cause.isUnauthenticated)) {
        console.error('Could not read the session', cause)
      }
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    // One place handles expiry for the whole app. A browser left open
    // overnight gets the sign-in prompt on its next call, rather than an
    // error in whichever screen happened to be open.
    setUnauthenticatedHandler(() => setUser(null))
    return () => setUnauthenticatedHandler(null)
  }, [])

  const signIn = useCallback(async (username: string, password: string) => {
    const info = await api.login(username, password)
    setUser(info.user)
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthValue>(
    () => ({ user, loading, signIn, signOut, refresh: load }),
    [user, loading, signIn, signOut, load],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
